import { useCallback } from 'react';
import { useParams } from 'react-router';
import { ShiftGeneratorTaskService } from 'src/api/ShiftGeneratorTaskService';
import type {
    ShiftGeneratorTask,
    ShiftGeneratorTaskParams,
    TaskPatch,
} from 'src/api/ShiftGeneratorTaskService';
import { invalidateShiftGeneratorTaskData } from '@stores/shiftGeneratorTaskStore';

type PatchTask = (
    taskId: string | undefined,
    patch: TaskPatch,
    params?: ShiftGeneratorTaskParams,
) => Promise<ShiftGeneratorTask | undefined>;

/*
 * Applies a JSON Patch to a task (settings, staffing, status) and drops
 * the cached task data on success. The task id is a per-call argument —
 * the task list patches a picked row, the detail and staffing views
 * patch their route task. The read params (e.g. includeTaskHolidays)
 * pass through to the endpoint; every caller continues with the
 * updated task response (local state updates), which useApiCall's void
 * requestApiCall cannot provide.
 */
const usePatchTask = (): { patchTask: PatchTask } => {
    const { departmentId } = useParams<{
        departmentId: string;
    }>();

    const patchTask = useCallback<PatchTask>(
        async (taskId, patch, params) => {
            if (!departmentId || !taskId) {
                throw new Error(
                    'usePatchTask requires a departmentId and taskId route param',
                );
            }

            const updatedTask = await ShiftGeneratorTaskService.patchTask(
                departmentId,
                taskId,
                patch,
                params,
            );

            if (!updatedTask) {
                return undefined;
            }

            invalidateShiftGeneratorTaskData();

            return updatedTask;
        },
        [departmentId],
    );

    return { patchTask };
};

export default usePatchTask;
